import type { ReactNode } from 'react'
import React, { useCallback, useMemo, useState } from 'react'
import { InputGroup } from '@systemeio/components'
import { Send03Icon } from '@systemeio/icons'
import { isKeyHotkey } from 'is-hotkey'
import { useTranslation } from 'next-i18next'
import type { Descendant } from 'slate'
import { Editor, Range, Transforms, createEditor } from 'slate'
import { withHistory } from 'slate-history'
import type { RenderElementProps, RenderLeafProps } from 'slate-react'
import { Editable, ReactEditor, Slate, withReact } from 'slate-react'
import { twJoin, twMerge } from 'tailwind-merge'
import { MentionSuggestions } from 'shared/components/suggest-mention'
import type { Suggestion } from 'shared/components/suggest-mention/types'
import { useDebouncedValue } from 'shared/hooks/use-debounced-value'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import { usePostAttachmentsContext } from 'modules/attachments/components/context'
import AttachmentsPreview from 'modules/attachments/components/preview'
import { EditorMode } from 'modules/comments/types'
import { isCommunityExtra } from 'modules/comments/utils/comments-extra-type-guards'
import { useSuggestionsList } from 'modules/community/api/use-suggestions-list'
import { deserializeTextHtml } from 'modules/community/hooks/slate-editor-utils'
import type { ParagraphElement } from 'modules/community/types/custom-editor'
import type { CommentEditorProps } from '.'
import { useCommentsContext } from '../comments-list'
import { Element } from './elements/element'
import { Leaf } from './elements/leaf'
import withLinks from './plugins/with-links'
import { withMentions } from './plugins/with-mentions'
import { Toolbar } from './toolbar'
import { insertMention } from './utils/mention'
import { serializeHTML } from './utils/serialize'

export const SlateCommentEditor = ({
  handleAddComment,
  setActiveComment,
  isRoot,
  initialHtml,
  initialMentions,
  mode = EditorMode.Add,
  onCancel,
  className = '',
  editableComment,
}: CommentEditorProps): ReactNode => {
  const { user } = useUser()
  const { t } = useTranslation()

  const [isFocused, setIsFocused] = useState(false)
  const [gifOpen, setGifOpen] = useState(false)
  const [emojiOpen, setEmojiOpen] = useState(false)
  const [linkOpen, setLinkOpen] = useState(false)
  const [videoOpen, setVideoOpen] = useState(false)
  const [target, setTarget] = useState<Range | null>(null)
  const [search, setSearch] = useState('')
  const parsedInitialValue = initialHtml
    ? deserializeTextHtml(initialHtml, initialMentions ?? [])
    : undefined
  const [value, setValue] = useState<Descendant[]>(
    Array.isArray(parsedInitialValue)
      ? parsedInitialValue
      : [
          {
            type: 'paragraph',
            children: [{ text: '' }],
          },
        ],
  )
  const serializedBlocks = value.map(node => serializeHTML(node))
  while (serializedBlocks.at(-1) === '<br>') serializedBlocks.pop()
  const serializedTextHtml = serializedBlocks.join('')

  const { extra } = useCommentsContext()
  const { attachments, isUploading } = usePostAttachmentsContext()
  const [debouncedSearch] = useDebouncedValue(search, 300)

  const editor = useMemo(() => withReact(withHistory(withLinks(withMentions(createEditor())))), [])
  const hasContent = serializedTextHtml && serializedTextHtml !== '<br>'
  const showExpandedView = isFocused || gifOpen || emojiOpen || linkOpen || videoOpen || hasContent
  const isCommunityComments = isCommunityExtra(extra)

  const getSuggestionsListQuery = useSuggestionsList({
    communityPath: isCommunityComments ? extra.community.communityPath : undefined,
    postId: isCommunityComments ? extra.community.postId : undefined,
    displayNameSearchBy: debouncedSearch,
  })

  const renderElement = useCallback((props: RenderElementProps) => <Element {...props} />, [])
  const renderLeaf = useCallback((props: RenderLeafProps) => {
    return <Leaf {...props} />
  }, [])

  const handleSendComment = (): void => {
    if (hasContent && !isUploading) {
      handleAddComment(serializedTextHtml, attachments)

      if (mode === EditorMode.Edit) return

      Transforms.select(editor, Editor.start(editor, []))
      editor.history = { redos: [], undos: [] }
      const emptyParagraph: ParagraphElement = {
        type: 'paragraph',
        children: [{ text: '' }],
      }
      editor.children = [emptyParagraph]
    }
  }

  const handleCancel = (): void => {
    if (onCancel) onCancel()

    setActiveComment(null)
  }

  const updateEditorContent = (value: Descendant[]): void => {
    setValue(value)
    const { selection } = editor

    // NOTE: This code for a display list of mentions when typing '@'
    if (selection && Range.isCollapsed(selection)) {
      const [start] = Range.edges(selection)
      const wordBefore = Editor.before(editor, start, { unit: 'word' })
      const before = wordBefore && Editor.before(editor, wordBefore)
      const beforeRange = before && Editor.range(editor, before, start)
      const beforeText = beforeRange && Editor.string(editor, beforeRange)
      const beforeMatch = beforeText?.match(/(?:^|\s)@([^\s@]*)(\s|$)/)
      const after = Editor.after(editor, start)
      const afterRange = Editor.range(editor, start, after)
      const afterText = Editor.string(editor, afterRange)
      const afterMatch = /^(\s|$)/.exec(afterText)

      const twoCharsBefore =
        wordBefore &&
        Editor.before(
          editor,
          { ...wordBefore, offset: wordBefore.offset },
          { unit: 'character', distance: 2 },
        )
      const twoCharsRange = twoCharsBefore && Editor.range(editor, twoCharsBefore, start)
      const twoCharsText = twoCharsRange && Editor.string(editor, twoCharsRange)

      const isEmptyBeforeAtRegular = new RegExp(/(?:^|\s)@/)
      const isEmptyBeforeAt = isEmptyBeforeAtRegular.test(twoCharsText || '')

      if (beforeMatch?.[1] && afterMatch && isEmptyBeforeAt) {
        // NOTE: This is for contains mention '@asd'
        setTarget(beforeRange || twoCharsRange || null)
        setSearch(beforeMatch[1])
        return
      } else {
        // NOTE: This is for empty mention '@'
        const pointBefore = Editor.before(editor, start, { distance: 2 })
        const pointRange = pointBefore && Editor.range(editor, pointBefore, start)
        const characterBefore = pointRange && Editor.string(editor, pointRange)

        const pointNormal = Editor.before(editor, start)
        const pointNormalRange = pointNormal && Editor.range(editor, pointNormal, start)

        if (characterBefore === ' @' || characterBefore === '@') {
          setTarget(pointNormalRange || pointRange || beforeRange || null)
          setSearch('')
          return
        }
      }
    }

    setTarget(null)
  }

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = event => {
    if (event.key === 'Enter' && event.ctrlKey && !event.nativeEvent.isComposing) {
      event.preventDefault()
      if (!event.repeat) handleSendComment()
      return
    }
    if (editableComment && event.key === 'Escape') {
      handleCancel()
      return
    }

    const { selection } = editor

    // Default left/right behavior is unit:'character'.
    // This fails to distinguish between two cursor positions, such as
    // <inline>foo<cursor/></inline> vs <inline>foo</inline><cursor/>.
    // Here we modify the behavior to unit:'offset'.
    // This lets the user step into and out of the inline without stepping over characters.
    // You may wish to customize this further to only use unit:'offset' in specific cases.
    // Reference from official Slate example: https://github.com/ianstormtaylor/slate/blob/6bc57583587d88b26fb03e054511b6d7ac23ef7c/site/examples/ts/inlines.tsx#L74
    if (selection && Range.isCollapsed(selection)) {
      const { nativeEvent } = event
      if (isKeyHotkey('left', nativeEvent)) {
        event.preventDefault()
        Transforms.move(editor, { unit: 'offset', reverse: true })
        return
      }
      if (isKeyHotkey('right', nativeEvent)) {
        event.preventDefault()
        Transforms.move(editor, { unit: 'offset' })
        return
      }
    }
  }

  const handleMentionSelect = (mention: Suggestion): void => {
    if (target) {
      insertMention(
        editor,
        {
          displayName: mention.displayName,
          id: mention.userId,
          profileImageUrl: mention.profileImageUrl,
        },
        target,
      )
      setTarget(null)
      ReactEditor.focus(editor)
    }
  }

  return (
    <Slate editor={editor} initialValue={value} onChange={updateEditorContent}>
      <div className={className} data-testid="comment-editor">
        <div className="flex flex-row items-start gap-2.5 sm:gap-2.5">
          {isAuthorizedUser(user) && (
            <img alt="avatar" className="mt-2 size-10 rounded-full" src={user.avatarUrl} />
          )}

          <InputGroup.Root
            onFocus={event => {
              setIsFocused(event.currentTarget.contains(event.target))
            }}
            onBlur={event => {
              setIsFocused(event.currentTarget.contains(event.relatedTarget))
            }}
            className={twMerge(
              'relative h-auto w-[calc(100%-40px)] min-w-0 grow-0 cursor-text flex-col items-stretch border border-transparent p-3 transition-all duration-200',
              (showExpandedView || mode === EditorMode.Edit) && 'p-5 pr-14 pb-12',
            )}
          >
            <div
              data-slot="input-group-control"
              data-focused={isFocused || undefined}
              className="pointer-events-none absolute size-0"
            />
            <Editable
              data-testid="comment-editor-input"
              autoFocus={mode === EditorMode.Edit}
              className="[&_a]:text-brand-primary comment-input max-h-[max(30svh,5rem)] w-full overflow-y-auto pr-2 text-start wrap-break-word whitespace-break-spaces text-text-primary focus-visible:outline-hidden [&_a]:no-underline [&_a:hover]:underline"
              dir="auto"
              placeholder={t('comments.components.comments.input_placeholder_label')}
              renderElement={renderElement}
              disableDefaultStyles={true}
              renderLeaf={renderLeaf}
              onKeyDown={onKeyDown}
              scrollSelectionIntoView={() => {
                // Do nothing because it is trigger scrolling window
                // see: https://docs.slatejs.org/libraries/slate-react/editable#scrollselectionintoview-editor-reacteditor-domrange-domrange-greater-than-void
              }}
            />
            <AttachmentsPreview className={twJoin(!!attachments?.length && `pt-4`)} />

            <Toolbar
              className={twJoin(
                'absolute bottom-2.5 left-3.5',
                !(showExpandedView || mode === EditorMode.Edit) && 'hidden',
              )}
              path={isCommunityComments ? extra.community.communityPath : undefined}
              onGifOpenChange={setGifOpen}
              onEmojiOpenChange={setEmojiOpen}
              onLinkOpenChange={setLinkOpen}
              onVideoOpenChange={setVideoOpen}
            />
            {(showExpandedView || mode === EditorMode.Edit) && (
              <>
                {mode !== EditorMode.Edit && !isRoot && (
                  <button
                    className="text-brand-primary hover:text-brand-primary-hover absolute right-3.5 bottom-2.5 block cursor-pointer text-sm md:hidden"
                    onClick={handleCancel}
                  >
                    {t('comments.components.comments.cancel_label')}
                  </button>
                )}
                <button
                  className="text-brand-primary absolute top-5 right-6 flex size-6 items-center justify-center disabled:text-text-secondary"
                  data-testid="comment-editor-submit"
                  onClick={handleSendComment}
                  disabled={!serializedTextHtml || serializedTextHtml === '<br>' || isUploading}
                >
                  <Send03Icon className="size-5 md:size-6" />
                </button>
              </>
            )}
            {isCommunityComments &&
              extra.community.communityPath &&
              target &&
              (getSuggestionsListQuery.isLoading || !!getSuggestionsListQuery.data?.length) && (
                <MentionSuggestions
                  target={target}
                  editor={editor}
                  items={getSuggestionsListQuery.data || []}
                  isLoading={getSuggestionsListQuery.isLoading}
                  onSelect={mention => {
                    handleMentionSelect(mention)
                  }}
                />
              )}
          </InputGroup.Root>
        </div>
        {(!isRoot || mode === EditorMode.Edit) && (
          <button
            data-testid="comment-editor-cancel"
            onClick={handleCancel}
            className="ml-12 hidden cursor-pointer pt-2.5 text-sm text-text-primary/50 transition-colors hover:text-text-primary md:inline-block"
            dangerouslySetInnerHTML={{
              __html: t('comments.components.comments.cancel_label_or_esc'),
            }}
          />
        )}
        {mode === EditorMode.Edit && (
          <div className="mt-2.5 flex items-center justify-end">
            <button
              className="bg-brand-primary text-background-secondary z-1 flex min-h-9 w-auto min-w-20 cursor-pointer items-center justify-end self-center rounded-sm px-5 text-center text-sm font-bold outline-0 select-none md:hidden"
              onClick={handleCancel}
            >
              {t('comments.components.comments.cancel_label')}
            </button>
          </div>
        )}
      </div>
    </Slate>
  )
}
